/**
 * Canonical derivation of the Push to EMR step's mode from intake state.
 *
 * The step's behavior (which handler the push button calls, which validation
 * gates apply, which alerts render) is a function of a handful of persisted
 * intake fields. Historically that function was a set of inline booleans in
 * CreateEpisodeStep whose fallthrough case was "create a new patient" — so any
 * field combination nobody anticipated (e.g. an existing patient whose
 * kinnser_visit_type was nulled by a prior "New Patient" confirmation) silently
 * landed in the create flow: wrong required-field errors and, worse, a push
 * button wired to create a duplicate patient. Deriving the mode here, in one
 * exhaustively-tested place, closes that class of bug.
 *
 * Taxonomy:
 * - `none`                — no EMR push selected; nothing to derive.
 * - `create`              — patient is new (or the new/existing decision has not
 *                           been made): create/onboard form and handlers govern.
 * - `awaiting_scheduling` — existing patient with an EMR selected but no visit
 *                           type picked yet on the Scheduling tab. Never a push
 *                           state: the create form does not apply, and pushing
 *                           must stay blocked until SOC/ROC is chosen.
 * - `roc`                 — existing patient, ROC scheduling on the current episode.
 * - `soc_existing`        — existing patient, SOC. On Kinnser this splits into an
 *                           execution strategy (see KinnserSocStrategy); on Roger
 *                           it is handled by Roger scheduling.
 *
 * ROC and SOC are EXISTING-patient concepts: the visit-type selector only
 * renders on the existing-patient Scheduling panel, and confirming "New
 * Patient" nulls kinnser_visit_type / kinnser_creation_type. A new or undecided
 * patient with a lingering visit type is therefore treated as `create` — new
 * patients always onboard (matching the auto_add guard this replaces), never
 * schedule onto an episode they don't have.
 *
 * EMR selection (Kinnser vs Roger) is deliberately NOT part of the mode: the
 * push flags are mutually exclusive in practice, and callers already pair the
 * mode with the individual flags (e.g. `kind === 'roc' && pushToRoger`).
 */

/** How a Kinnser SOC on an existing patient is executed. */
export type KinnserSocStrategy =
  /** Create the admission + episode (and SOC task) via the auto_add flow. */
  | 'auto_add'
  /** Schedule the SOC task on the patient's current Kinnser episode. */
  | 'task_on_episode';

export type PushMode =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'awaiting_scheduling' }
  | { kind: 'roc' }
  /**
   * NEW-patient recert onboarding pushed to Roger: creates the patient plus a
   * back-dated admission, previous + upcoming episodes, and a REC visit. A
   * create flow (same handler/gating as `create`), distinguished for labels
   * and the pre-push summary. Roger-only: rec + Kinnser-only derives `create`
   * (the Scheduling header prevents that combination).
   */
  | { kind: 'rec' }
  | {
      kind: 'soc_existing';
      /** Set when pushing to Kinnser; null for a Roger-only SOC. */
      kinnserStrategy: KinnserSocStrategy | null;
    };

export interface PushModeInput {
  /** Raw intake.is_new_patient: true = new, false = existing, null = undecided. */
  isNewPatient: boolean | null;
  /** Shared visit-type key (kinnser_visit_type; also used for Roger scheduling). */
  visitType: string | null;
  /** Shared creation-type key (kinnser_creation_type). */
  creationType: string | null;
  pushToKinnser: boolean;
  pushToRoger: boolean;
}

export function derivePushMode({
  isNewPatient,
  visitType,
  creationType,
  pushToKinnser,
  pushToRoger,
}: PushModeInput): PushMode {
  if (!pushToKinnser && !pushToRoger) return { kind: 'none' };

  const isExistingPatient = isNewPatient === false;
  if (!isExistingPatient) {
    // REC (recert onboarding) is a new-patient create flow with its own
    // records shape; it exists only for Roger pushes.
    if (visitType === 'rec' && pushToRoger) return { kind: 'rec' };
    return { kind: 'create' };
  }

  if (visitType === 'roc') return { kind: 'roc' };
  if (visitType === 'soc') {
    return {
      kind: 'soc_existing',
      kinnserStrategy: pushToKinnser
        ? creationType === 'new_admission' || creationType === 'new_episode'
          ? 'auto_add'
          : 'task_on_episode'
        : null,
    };
  }

  return { kind: 'awaiting_scheduling' };
}
